/**
 * Fase 2 «Conduta e Contexto» — rótulos PT-BR dos 5 eixos.
 *
 * ── Neutralidade é requisito, não estilo ──────────────────────────────────────────────
 * Todo rótulo e toda descrição aqui passa pelo teste: **serve a uma clínica, a um
 * escritório e a uma loja ao mesmo tempo?** Por isso «cliente» e nunca «paciente»/
 * «assistido»/«comprador»; «atendimento» e nunca «consulta»/«caso»/«pedido». O termo do
 * motor para a relação com o cliente é **vínculo**, nunca «contrato».
 *
 * ── Só o que o enum tem ───────────────────────────────────────────────────────────────
 * As opções são exatamente os valores dos enums do schema. Nenhum rótulo inventado,
 * nenhuma opção a mais — mesma regra que fez a Fase 0b recusar `description`/`idioma`/`tags`
 * do mockup. `confirmationStyle` tem 2 valores (não 3) porque o enum tem 2.
 *
 * Molde: `personas/persona-labels.ts` (mapa de rótulo + mapa de descrição + acessor
 * defensivo) e `playbooks/playbook-labels.ts` (`*_OPTIONS` com ordem explícita).
 */

import { CONDUCT_AXES } from './conduct-types';

import type { ConductAxis, ConductOriginLevel } from './conduct-types';

/** Uma opção de eixo: o valor do enum + como o operador a lê. */
export interface ConductOption {
  readonly value: string;
  readonly label: string;
  /** O que MUDA na fala da IA. Escrito do ponto de vista do cliente, não do mecanismo. */
  readonly desc: string;
}

/** Um eixo: como se chama, o que governa, e as opções em ordem deliberada. */
export interface ConductAxisMeta {
  readonly axis: ConductAxis;
  readonly label: string;
  /** A pergunta que este eixo responde — é o que faz o operador saber se é o que ele quer. */
  readonly question: string;
  readonly options: readonly ConductOption[];
}

export const CONDUCT_AXIS_META: readonly ConductAxisMeta[] = [
  {
    axis: 'registerPolicy',
    label: 'Sintonia com o cliente',
    question: 'A IA acompanha o jeito de falar do cliente, ou mantém sempre o tom da casa?',
    options: [
      {
        value: 'hold_persona',
        label: 'Mantém o tom da casa',
        desc: 'O tom não muda com o cliente. A IA se adapta no conteúdo (o que explicar, com que profundidade), nunca na forma.',
      },
      {
        value: 'mirror_within_persona',
        label: 'Acompanha, sem sair do tom da casa',
        desc: 'A IA se aproxima do jeito do cliente, mas dentro dos limites do tom definido. É o comportamento atual da plataforma.',
      },
      {
        value: 'mirror_client',
        label: 'Acompanha o cliente de perto',
        desc: 'Espelha comprimento, registro e ritmo do cliente. Nunca acompanha hostilidade, e os limites de tom continuam invioláveis.',
      },
    ],
  },
  {
    axis: 'guidanceStyle',
    label: 'Como a IA conduz',
    question: 'A conversa flui em texto aberto, ou a IA oferece opções para o cliente escolher?',
    options: [
      {
        value: 'conversational',
        label: 'Conversa aberta',
        desc: 'Sem menu de opções. A IA acolhe e pergunta em texto corrido. É o comportamento atual da plataforma.',
      },
      {
        value: 'menus_allowed',
        label: 'Conversa, com opções quando ajudar',
        desc: 'Prefere conversa, mas pode oferecer uma lista curta quando o cliente estiver perdido ou os caminhos forem fechados. Nunca abre o atendimento com menu.',
      },
      {
        value: 'menus_preferred',
        label: 'Conduz por opções',
        desc: 'Depois de acolher, conduz por escolhas claras. Pergunta aberta só quando nenhuma opção couber.',
      },
    ],
  },
  {
    axis: 'responseLength',
    label: 'Tamanho das respostas',
    question: 'A IA responde no essencial ou explica o contexto?',
    options: [
      {
        value: 'short',
        label: 'Curtas',
        desc: 'De 1 a 3 frases, direto ao ponto. Explicação longa só se o cliente pedir.',
      },
      {
        value: 'standard',
        label: 'Padrão',
        desc: 'O equilíbrio atual da plataforma. Sem instrução extra de extensão.',
      },
      {
        value: 'detailed',
        label: 'Completas',
        desc: 'Inclui o contexto útil (o que vai acontecer, por quê, próximo passo) quando a pergunta comportar. Sem virar relatório.',
      },
    ],
  },
  {
    axis: 'confirmationStyle',
    label: 'Confirmação dos dados',
    question: 'A IA repete o dado para o cliente confirmar, ou já age com ele à vista?',
    options: [
      {
        value: 'echo',
        label: 'Repete para confirmar',
        desc: 'Ecoa o dado coletado antes de seguir. É o comportamento atual da plataforma.',
      },
      {
        value: 'implicit',
        label: 'Confirma agindo',
        desc: 'Incorpora o dado na frase da ação em vez de parar para confirmar. Datas, chave de verificação e argumentos de ação SEMPRE continuam sendo ecoados — isso não se afrouxa.',
      },
    ],
  },
  {
    axis: 'escalationPosture',
    label: 'Quando chamar uma pessoa',
    question: 'Na dúvida, a IA passa para um humano mais cedo ou tenta resolver antes?',
    options: [
      {
        value: 'conservative',
        label: 'Passa mais cedo',
        desc: 'Na dúvida, prefere chamar uma pessoa a tentar resolver. Indicado quando errar custa mais que transferir à toa.',
      },
      {
        value: 'balanced',
        label: 'Equilibrada',
        desc: 'O critério atual da plataforma.',
      },
      {
        value: 'autonomous',
        label: 'Tenta resolver antes',
        desc: 'Esgota o que tem mecanismo para resolver antes de passar. Os casos que SEMPRE exigem uma pessoa continuam valendo — a postura não os afrouxa.',
      },
    ],
  },
];

/** Acesso por eixo, sem repetir a busca em cada componente. */
export function conductAxisMeta(axis: ConductAxis): ConductAxisMeta {
  const meta = CONDUCT_AXIS_META.find((m) => m.axis === axis);
  if (meta === undefined) throw new Error(`eixo de conduta sem metadados: ${axis}`);
  return meta;
}

/** Rótulo de um valor de eixo; cai no valor cru se o enum crescer sem passar por aqui. */
export function conductValueLabel(axis: ConductAxis, value: string | null): string {
  if (value === null) return '—';
  return conductAxisMeta(axis).options.find((o) => o.value === value)?.label ?? value;
}

/**
 * Como se nomeia cada nível na frase de origem. Sem nome próprio do nível quando ele tem
 * um (a origem é montada como «herdado do Playbook: Suporte» em `originSentence`).
 */
export const CONDUCT_LEVEL_LABEL: Readonly<Record<ConductOriginLevel, string>> = {
  persona: 'Persona',
  playbook: 'Playbook',
  tenant: 'Configuração do tenant',
  platform: 'Padrão da plataforma',
};

/**
 * A frase que mata a ambiguidade do campo em branco.
 *
 * Um seletor vazio não distingue «não configurei» de «configurei com o valor que por acaso
 * é o default». Esta frase resolve isso em toda tela: ou diz «definido aqui», ou nomeia
 * exatamente de onde o valor vem.
 */
export function originSentence(
  origin: ConductOriginLevel,
  viewingLevel: ConductOriginLevel,
  levelName: string | null,
): string {
  if (origin === viewingLevel) return 'Definido aqui, neste nível.';
  if (origin === 'platform') return 'Herdado do padrão da plataforma.';
  const named =
    levelName === null
      ? CONDUCT_LEVEL_LABEL[origin]
      : `${CONDUCT_LEVEL_LABEL[origin]}: ${levelName}`;
  return `Herdado de ${named}.`;
}

/** Por que um nível não participa — em português de operador, não de engenheiro. */
export const CONDUCT_ABSENT_REASON_TEXT: Readonly<Record<string, string>> = {
  no_playbook_selected: 'Nenhum playbook selecionado, então este nível não entra na conta.',
  playbook_not_active:
    'Este playbook não está ativo — o que estiver configurado nele NÃO afeta a fala da IA enquanto isso.',
  playbook_has_no_persona: 'Este playbook não usa persona, então não há esse nível para ajustar.',
  persona_not_active:
    'A persona deste playbook não está ativa — o que estiver configurado nela NÃO afeta a fala da IA.',
  persona_level_gated_off:
    'O plano atual não usa a voz da persona na condução da IA. O que estiver configurado na persona fica guardado, mas NÃO afeta a fala hoje.',
};

export function absentReasonText(reason: string | null): string | null {
  if (reason === null) return null;
  return CONDUCT_ABSENT_REASON_TEXT[reason] ?? null;
}

/** Guard de completude: todo eixo do contrato tem metadados de tela. */
export const CONDUCT_AXES_WITH_META: readonly ConductAxis[] = CONDUCT_AXES.filter((a) =>
  CONDUCT_AXIS_META.some((m) => m.axis === a),
);
